import { flatten, unflatten } from 'flat';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LIMIT_OPTIONS, OrderMethodEnum } from '../constants';

export interface FiltersInterface extends Record<string, any> {
  order?: OrderMethodEnum;
  sortBy?: string;
  limit?: number;
  page?: number;
}

export interface SearchParamsContextProps {
  searchParams: FiltersInterface;
  updateSearchParams: (params: FiltersInterface) => void;
  clearSearchParams: () => void;
  sortHandler?: (column: string, sortMethod: OrderMethodEnum) => void;
}

export const SearchParamsContext = createContext<SearchParamsContextProps>({
  searchParams: {},
  updateSearchParams: () => {},
  clearSearchParams: () => {},
});

export const useSearchParamsContext = () => useContext(SearchParamsContext);

interface SearchParamsContextProviderProps {
  children: ReactNode;
  defaultParams?: FiltersInterface;
}

export const SearchParamsContextProvider = ({
  children,
  defaultParams,
}: SearchParamsContextProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaults: FiltersInterface = useMemo(
    () => ({
      page: 1,
      limit: LIMIT_OPTIONS[1],
    }),
    []
  );

  const updateSearchParams = useCallback(
    (params: FiltersInterface) => {
      setSearchParams(
        flatten({
          ...Object.fromEntries([...searchParams]),
          ...params,
        })
      );
    },
    [searchParams, setSearchParams]
  );

  const clearSearchParams = useCallback(
    () => setSearchParams(defaults),
    [defaults, setSearchParams]
  );

  const value = useMemo(
    () => ({
      searchParams: unflatten<FiltersInterface, FiltersInterface>({
        ...defaults,
        ...defaultParams,
        ...Object.fromEntries([...searchParams]),
      }),
      updateSearchParams,
      clearSearchParams,
    }),
    [clearSearchParams, defaultParams, defaults, searchParams, updateSearchParams]
  );

  return <SearchParamsContext.Provider value={value}>{children}</SearchParamsContext.Provider>;
};
